import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import GenericFormDrawer from '../../../components/GenericFormDrawer';
import Button from '../../../components/ui/Button';
import { ProgressFill, ProgressTrack, Sub, Table } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { formatDate } from '../../../utils/Utils';
import {
  fetchUnitDetail, formatNum, formatPct, metricByCode, metricText, useBandColors,
} from '../../performance-results/config';

// PM analitik P2 — E10 sağ çekmece: seçili birimin zaman serisi (seçili dönem + öncesindeki en çok 5 dönem; tamamlanma, hedef
// puanı ve normalize final ortalaması — farklı ölçekli dönemler normalize değerle kıyaslanır), rol karması ve ilgili süreç
// aksiyonları (birime bağlı aksiyonlar, geciken görev sayısı → E02). Seri her noktada tablo ile aynı gizleme kuralını taşır.
// Salt okunur kabuk GenericFormDrawer (modal yok); birincil düğme "Kapat".
const Section = styled.h4`
  margin: 18px 0 8px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RowLabel = styled.span`
  width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Links = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const SERIES = ['M02', 'M09', 'M60'];

export default function UnitDetailDrawer({
  open, onClose, filters, groupBy, parentKey, unit, employeesHref, tasksHref, onDrill,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const theme = useTheme();
  const colors = useBandColors();
  const [table, setTable] = useState(false);
  const unitKey = unit?.key;
  const key = JSON.stringify([filters, groupBy, parentKey, unitKey]);
  const panel = usePanel(
    () => fetchUnitDetail(filters, { groupBy, parentEmployeeId: groupBy === 'hierarchy' ? parentKey : null, unitKey }),
    [key],
    open && !!unitKey && !!filters.periodId,
  );
  const data = panel.data;
  const lineColor = { M02: colors.goal, M09: colors.competency, M60: colors.combined };
  const chartRows = (data?.series || []).map((p) => {
    const row = { name: p.periodName, periodId: p.periodId };
    SERIES.forEach((c) => {
      const m = metricByCode(p.metrics, c);
      row[c] = m && !m.suppressionReason && m.value !== null && m.value !== undefined ? Number(m.value) : null;
    });
    return row;
  });

  const title = data?.label || unit?.label || t('performance.orgComparison.detail.title');

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={title}
      schema={[]}
      submitLabel={t('common.close')}
      onSave={async () => true}
    >
      <div data-drawer="unit-detail">
        {panel.loading ? <Sub data-state="loading">{t('common.loading')}</Sub> : null}
        {panel.error ? (
          <>
            <Sub data-state="error">{t('performance.operations.panel.error')}</Sub>
            <Button variant="outline" onClick={panel.retry} data-action="retry">{t('performance.operations.panel.retry')}</Button>
          </>
        ) : null}
        {data ? (
          <>
            <Section>{t('performance.orgComparison.detail.series')}</Section>
            <Button variant="ghost" onClick={() => setTable((v) => !v)} data-action="toggle-table">
              {table ? t('performance.operations.panel.showChart') : t('performance.operations.panel.showTable')}
            </Button>
            {table ? (
              <Table data-view="series-table">
                <thead>
                  <tr>
                    <th>{t('performance.orgComparison.detail.period')}</th>
                    <th>{t('performance.results.analytics.metric.M02')}</th>
                    <th>{t('performance.results.analytics.metric.M06')}</th>
                    <th>{t('performance.results.analytics.metric.M09')}</th>
                    <th>{t('performance.results.analytics.metric.M60')}</th>
                    <th>N</th>
                  </tr>
                </thead>
                <tbody>
                  {data.series.map((p) => (
                    <tr key={p.periodId} data-row={p.periodId}>
                      <td>{p.periodName}</td>
                      {p.unavailableReason ? (
                        <td colSpan={5}>{t(`performance.orgComparison.detail.unavailable.${p.unavailableReason}`)}</td>
                      ) : (
                        <>
                          <td>{metricText(t, metricByCode(p.metrics, 'M02'), lang)}</td>
                          <td>{metricText(t, metricByCode(p.metrics, 'M06'), lang)}</td>
                          <td>{metricText(t, metricByCode(p.metrics, 'M09'), lang)}</td>
                          <td>{metricText(t, metricByCode(p.metrics, 'M60'), lang)}</td>
                          <td>{formatNum(p.eligibleN, lang, 0)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div style={{ width: '100%', height: 220 }} data-view="series-chart">
                <ResponsiveContainer>
                  <LineChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: theme.metrics.font.size.xs }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: theme.metrics.font.size.xs }} />
                    <Tooltip formatter={(value) => formatNum(value, lang)} />
                    <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
                    {SERIES.map((c) => (
                      <Line
                        key={c} type="monotone" dataKey={c} name={t(`performance.results.analytics.metric.${c}`)} stroke={lineColor[c]}
                        strokeWidth={2} dot={{ r: 4 }} connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <Sub data-note="series">{t('performance.orgComparison.detail.seriesNote')}</Sub>

            <Section>{t('performance.orgComparison.detail.roleMix')}</Section>
            {data.roleMix.length === 0 ? <Sub>{t('performance.results.analytics.noData')}</Sub> : data.roleMix.map((r) => (
              <Row key={r.key || 'none'} data-role={r.key || 'none'}>
                <RowLabel title={r.label || ''}>
                  {r.key === 'other' ? t('performance.orgComparison.detail.otherRoles') : (r.label || t('performance.orgComparison.detail.noTitle'))}
                </RowLabel>
                <ProgressTrack><ProgressFill $pct={Number(r.pct || 0)} /></ProgressTrack>
                <span>{`${formatNum(r.count, lang, 0)} · ${formatPct(r.pct, lang)}`}</span>
              </Row>
            ))}

            <Section>{t('performance.orgComparison.detail.process')}</Section>
            <Sub data-overdue={data.overdueTasks}>
              {t('performance.orgComparison.detail.overdue', { tasks: formatNum(data.overdueTasks, lang, 0), persons: formatNum(data.overduePersons, lang, 0) })}
            </Sub>
            {data.actions.length === 0 ? <Sub>{t('performance.orgComparison.detail.noActions')}</Sub> : (
              <Table data-view="actions">
                <thead>
                  <tr>
                    <th>{t('performance.operations.col.action')}</th>
                    <th>{t('performance.operations.col.owner')}</th>
                    <th>{t('performance.operations.col.due')}</th>
                    <th>{t('performance.operations.col.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.actions.map((a) => (
                    <tr key={a.id} data-row={a.id}>
                      <td title={a.title}>{a.title}</td>
                      <td>{a.ownerName || '-'}</td>
                      <td>{a.dueDate ? formatDate(a.dueDate, lang) : '-'}</td>
                      <td>{t(`performance.action.statusValue.${a.status}`, { defaultValue: a.status })}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Links>
              {employeesHref ? <Link to={employeesHref} data-link="employees">{t('performance.orgComparison.action.employees')}</Link> : null}
              {tasksHref ? <Link to={tasksHref} data-link="tasks">{t('performance.orgComparison.detail.openTasks')}</Link> : null}
              {unit?.hasChildren && onDrill ? (
                <Button variant="ghost" onClick={() => onDrill(unit)} data-action="drill">{t('performance.orgComparison.action.drill')}</Button>
              ) : null}
            </Links>
          </>
        ) : null}
      </div>
    </GenericFormDrawer>
  );
}
